import React, { useState } from "react";
import { Twitter, Linkedin, Link as LinkIcon, MessageSquare, TrendingUp, Check } from "lucide-react";

/**
 * MOCKUP — Shareable Win Cards (WIP)
 * Route: /win-preview  (public for now while we iterate on design)
 *
 * Shows 3 variants of the share card so we can pick a direction before
 * wiring the real Pillow renderer + /api/track-record/{id}/card.png.
 */
export default function WinPreview() {
  return (
    <div className="min-h-screen bg-mb-sand py-10 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="uppercase tracking-widest text-[11px] text-mb-violet font-bold">Marketing mockup · not shipped</div>
        <h1 className="text-4xl font-bold tracking-tight text-mb-ink mt-2">Shareable Win Cards</h1>
        <p className="mt-3 text-neutral-600 max-w-2xl">
          Every closed winning alert becomes a share-ready card. Subscribers post to
          Twitter/LinkedIn/Discord — each share = free organic reach to their
          finance-curious followers with a referral link back to Moonbucks.
        </p>

        <h2 className="mt-10 mb-4 text-sm uppercase tracking-widest text-neutral-600 font-bold">
          Variant A · Bold moonshot — the "hero" card
        </h2>
        <CardBold />
        <MockShareBar />

        <h2 className="mt-12 mb-4 text-sm uppercase tracking-widest text-neutral-600 font-bold">
          Variant B · Data-desk — proof-of-work aesthetic
        </h2>
        <CardData />
        <MockShareBar />

        <h2 className="mt-12 mb-4 text-sm uppercase tracking-widest text-neutral-600 font-bold">
          Variant C · Minimal ticker card — Twitter-native
        </h2>
        <CardMinimal />
        <MockShareBar />

        <div className="mt-14 rounded-md border border-mb-border bg-white p-6 text-sm text-neutral-700">
          <div className="font-bold text-mb-ink mb-2">Real flow after we ship:</div>
          <ol className="list-decimal list-inside space-y-1">
            <li>Alert closes → backend renders a 1200×630 PNG (OG-image standard) and stores it</li>
            <li>Subscriber sees "Share this win" button on their Recent Exits card</li>
            <li>One tap → Twitter/LinkedIn/Discord pre-filled with the image + copy + <code className="text-mb-violet">?ref=USER123</code> link</li>
            <li>Follower clicks → lands on <code className="text-mb-violet">/win/&#123;id&#125;</code> deep page with the trade + "See what's on the desk" CTA</li>
            <li>Signup uses referral code → both get a month free</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

/* ─── Variant A: Bold moonshot ────────────────────────────────────── */
function CardBold() {
  return (
    <div
      className="rounded-2xl overflow-hidden shadow-2xl"
      style={{
        width: 1200, maxWidth: "100%", aspectRatio: "1200/630",
        background: "radial-gradient(circle at top right, #1F0B47 0%, #0A0518 65%)",
        border: "1px solid rgba(245,197,106,0.25)",
      }}
    >
      <div className="h-full p-12 flex flex-col justify-between text-white" style={{fontFamily: "'Inter', sans-serif"}}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: "#FDE68A"}}>
              <span className="text-black font-bold text-lg">M</span>
            </div>
            <span className="text-xl font-bold tracking-tight">moonbucks</span>
          </div>
          <div className="uppercase tracking-widest text-[11px] font-bold" style={{color:"#F5C56A"}}>
            Winning alert · closed
          </div>
        </div>
        <div>
          <div className="text-[15px] uppercase tracking-widest text-white/60 font-bold">
            NVDA · Semiconductor breakout
          </div>
          <div className="mt-3 flex items-baseline gap-5">
            <div style={{fontSize: 96, fontWeight: 800, letterSpacing: "-0.03em", color: "#6EE7A0", lineHeight: 1}}>
              +18.4%
            </div>
            <div className="text-white/60">
              <div className="font-mono-data text-sm">$214.20 → $253.60</div>
              <div className="text-xs">held 12 days · closed 09/03</div>
            </div>
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div className="text-white/70 text-sm leading-snug max-w-md">
            <span className="font-bold text-white">Conviction Stack 3/5:</span> insider buys ·
            options flow · analyst uptick — all three fired on entry.
          </div>
          <div className="text-right">
            <div className="uppercase tracking-widest text-[10px] text-white/50">See what's on the desk</div>
            <div className="text-lg font-bold" style={{color:"#FDE68A"}}>moonbucks.io/track</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Variant B: Data-desk ────────────────────────────────────────── */
function CardData() {
  return (
    <div
      className="rounded-2xl overflow-hidden shadow-xl"
      style={{
        width: 1200, maxWidth: "100%", aspectRatio: "1200/630",
        background: "#FBF7EC",
        border: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      <div className="h-full p-12 flex flex-col justify-between" style={{fontFamily: "'Inter', sans-serif"}}>
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-mb-ink">moonbucks</span>
          <div className="uppercase tracking-widest text-[10px] font-bold text-mb-violet">Analyst desk · trade closed</div>
        </div>
        <div className="grid grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[15px] uppercase tracking-widest text-neutral-500 font-bold">NVDA</div>
            <div className="text-neutral-600">NVIDIA Corp · Semiconductors</div>
            <div className="mt-4" style={{fontSize: 84, fontWeight: 800, letterSpacing: "-0.03em", color: "#059669", lineHeight: 1}}>
              +18.4%
            </div>
            <div className="text-sm text-neutral-600 font-mono-data mt-2">
              $214.20 entry → $253.60 exit · 12 day hold
            </div>
          </div>
          <div className="space-y-2 font-mono-data text-sm">
            <MetaRow label="Conviction grade" value="A · 3 of 5 fired" />
            <MetaRow label="Alert class" value="Moonshot" />
            <MetaRow label="Confidence" value="82%" />
            <MetaRow label="Entered" value="in-zone · $216 stop" />
            <MetaRow label="TP1 hit" value="day 4 · +8.6%" />
            <MetaRow label="Closed at" value="TP2 · +18.4%" />
          </div>
        </div>
        <div className="flex items-end justify-between border-t border-neutral-200 pt-4">
          <div className="text-xs text-neutral-500 max-w-md leading-snug">
            Not personalized advice · full track record + methodology at moonbucks.io/track
          </div>
          <div className="text-lg font-bold text-mb-ink">moonbucks.io</div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({label, value}) {
  return (
    <div className="flex justify-between border-b border-neutral-200 pb-1">
      <span className="text-neutral-500">{label}</span>
      <span className="text-mb-ink font-bold">{value}</span>
    </div>
  );
}

/* ─── Variant C: Minimal Twitter-native ────────────────────────────── */
function CardMinimal() {
  return (
    <div
      className="rounded-2xl overflow-hidden shadow-lg"
      style={{
        width: 1200, maxWidth: "100%", aspectRatio: "2/1",
        background: "#0A0518",
        border: "1px solid rgba(245,197,106,0.35)",
      }}
    >
      <div className="h-full p-14 flex items-center justify-between text-white" style={{fontFamily: "'Inter', sans-serif"}}>
        <div>
          <div className="uppercase tracking-widest text-[13px] font-bold" style={{color:"#F5C56A"}}>
            moonbucks · win
          </div>
          <div className="mt-4 text-white/80 text-2xl">NVDA · Semis breakout</div>
          <div className="mt-6" style={{fontSize: 128, fontWeight: 800, letterSpacing: "-0.04em", color: "#6EE7A0", lineHeight: 0.95}}>
            +18.4%
          </div>
          <div className="mt-5 text-white/60 font-mono-data text-lg">$214 → $253 · 12 days</div>
        </div>
        <div className="text-right">
          <div className="w-32 h-32 rounded-full flex items-center justify-center mx-auto" style={{background: "linear-gradient(135deg,#FDE68A,#F59E0B)"}}>
            <TrendingUp className="w-16 h-16 text-black" strokeWidth={2.5} />
          </div>
          <div className="mt-6 text-white/50 uppercase tracking-widest text-[10px] font-bold">Alerts vetted daily</div>
          <div className="text-xl font-bold" style={{color:"#FDE68A"}}>moonbucks.io</div>
        </div>
      </div>
    </div>
  );
}

/* ─── Mock share bar (not functional yet) ─────────────────────────── */
function MockShareBar() {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-4 flex items-center gap-2">
      <span className="text-xs uppercase tracking-widest text-neutral-500 font-bold mr-2">Share to</span>
      <button className="p-2 rounded-md bg-white border border-mb-border hover:bg-blue-50 hover:border-blue-300" title="Twitter">
        <Twitter className="w-4 h-4 text-blue-500" />
      </button>
      <button className="p-2 rounded-md bg-white border border-mb-border hover:bg-blue-50 hover:border-blue-300" title="LinkedIn">
        <Linkedin className="w-4 h-4 text-blue-700" />
      </button>
      <button className="p-2 rounded-md bg-white border border-mb-border hover:bg-indigo-50 hover:border-indigo-300" title="Discord">
        <MessageSquare className="w-4 h-4 text-indigo-500" />
      </button>
      <button
        className="px-3 py-2 rounded-md bg-white border border-mb-border hover:bg-neutral-50 flex items-center gap-1.5 text-xs font-mono-data text-neutral-700"
        onClick={() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {copied ? <Check className="w-3.5 h-3.5 text-mb-emerald-dark" /> : <LinkIcon className="w-3.5 h-3.5" />}
        {copied ? "Copied" : "moonbucks.io/win/nvda-0903?ref=you"}
      </button>
    </div>
  );
}
